  import { db } from "../backend/firebaseConfig";
import { collection, doc, getDoc, getDocs, updateDoc } from "firebase/firestore";
import { addNotification } from "../components/functions/notifyComp";
import {
  clockLabelsEqual,
  formatClock12FromMinutes,
  formatClockLabelForDisplay,
  getAppointmentDisplayRangeMinutes,
  parseClockLabelToMinutes,
} from "./appointmentTimeDisplay";

const CLINIC_NAME = "VSDiamond Dental Clinic";

/** Prevent duplicate app/email bursts when listener + booking both compress within a short window. */
const recentNotifyKeys = new Map();
const NOTIFY_DEDUPE_MS = 120_000;

function formatDateLong(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = String(dateStr).split("-");
  const date = new Date(year, month - 1, day);
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  return `${monthNames[date.getMonth()]} ${day}, ${year}`;
}

function buildNotificationWindow(dateLabel, startTime, endTime) {
  const safeStart = startTime || "N/A";
  const safeEnd = endTime || "N/A";
  return `${dateLabel} (${safeStart} - ${safeEnd})`;
}

function isEmailVerified(userData) {
  return userData?.emailVerified === "Yes" || userData?.emailVerified === true;
}

function resolveNotificationEmail(userData) {
  const email = userData?.email || userData?.authEmail || "";
  if (!email || String(email).includes("@mobileuser.dentalapp.com")) {
    return null;
  }
  return email;
}

function notifyDedupeKey(adjustment) {
  return [
    adjustment.id,
    formatClockLabelForDisplay(adjustment.originalStart),
    formatClockLabelForDisplay(adjustment.originalEnd),
    formatClockLabelForDisplay(adjustment.startTime),
    formatClockLabelForDisplay(adjustment.endTime),
  ].join("|");
}

function shouldSkipDuplicateNotify(adjustment) {
  const key = notifyDedupeKey(adjustment);
  const last = recentNotifyKeys.get(key);
  if (last != null && Date.now() - last < NOTIFY_DEDUPE_MS) {
    return true;
  }
  recentNotifyKeys.set(key, Date.now());
  return false;
}

function getStatusChangeApiUrl() {
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  return `${base}/api/send-appointment-status-change`;
}

function normalizeServiceNames(services) {
  if (!Array.isArray(services)) {
    if (typeof services === "string" && services.trim()) {
      return [services.trim()];
    }
    return [];
  }
  return services
    .map((service) => {
      if (typeof service === "string") {
        return service.trim();
      }
      if (service && typeof service === "object") {
        return (service.name || service.serviceName || "").trim();
      }
      return String(service || "").trim();
    })
    .filter(Boolean);
}

function formatServicesForEmail(services) {
  const names = normalizeServiceNames(services);
  return names.length ? names.join(", ") : "\u2014";
}

function isPlaceholderDentistLabel(name) {
  if (name == null || typeof name !== "string") return true;
  const t = name.trim();
  if (!t) return true;
  const u = t.toUpperCase();
  return u === "N/A" || u === "\u2014" || u === "-";
}

function resolveDentistDisplayName(adjustment, usersSnapshot) {
  if (adjustment.dentistName && !isPlaceholderDentistLabel(adjustment.dentistName)) {
    return adjustment.dentistName;
  }
  const dentistId = adjustment.dentistId;
  if (!dentistId || !usersSnapshot?.docs) {
    return !isPlaceholderDentistLabel(adjustment.dentistName)
      ? adjustment.dentistName
      : "\u2014";
  }
  const dentistDoc = usersSnapshot.docs.find((entry) => entry.id === dentistId);
  if (!dentistDoc) {
    return !isPlaceholderDentistLabel(adjustment.dentistName)
      ? adjustment.dentistName
      : "\u2014";
  }
  const data = dentistDoc.data();
  const name =
    data.firstName && data.lastName
      ? `${data.firstName} ${data.lastName}`
      : data.name || "";
  if (!name) {
    return !isPlaceholderDentistLabel(adjustment.dentistName)
      ? adjustment.dentistName
      : "\u2014";
  }
  const role = data.userType || data.role;
  return role === "DentistOwner" || role === "AssociateDentist"
    ? `Dr. ${name}`
    : name;
}

async function sendAutoAdjustmentEmail(payload) {
  try {
    const response = await fetch(getStatusChangeApiUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        isAutoAdjustment: true,
        clinicName: CLINIC_NAME,
        actorName: "System",
        ...payload,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        `HTTP error sending auto-adjustment email (${payload.type}):`,
        response.status,
        errorText
      );
      return false;
    }

    const result = await response.json();
    if (!result.success) {
      console.error(
        `Failed to send auto-adjustment email (${payload.type}):`,
        result.message
      );
      return false;
    }
    return true;
  } catch (error) {
    console.error(`Error sending auto-adjustment email (${payload.type}):`, error);
    return false;
  }
}

/**
 * Pick non-empty services from an appointment row (`services` or singular `service`).
 */
function pickServicesFromAppointmentRecord(src) {
  if (!src || typeof src !== "object") return [];
  const singular =
    typeof src.service === "string" && src.service.trim()
      ? [src.service.trim()]
      : Array.isArray(src.service)
        ? src.service
        : [];
  const candidates = [src.services, singular];
  for (const c of candidates) {
    if (Array.isArray(c) && c.length > 0) return c;
  }
  return [];
}

/**
 * Ensure chain updates include booked (pre-shift) times for notifications.
 * Forward/backward compression paths may only pass startTime/endTime (new times).
 */
export function enrichChainUpdateWithOriginalTimes(update, sourceAppt = null) {
  if (!update?.id) return null;

  const startTime = formatClockLabelForDisplay(update.startTime);
  const endTime = formatClockLabelForDisplay(update.endTime);
  let originalStart = formatClockLabelForDisplay(update.originalStart);
  let originalEnd = formatClockLabelForDisplay(update.originalEnd);

  if (!originalStart || !originalEnd) {
    const range = sourceAppt ? getAppointmentDisplayRangeMinutes(sourceAppt) : null;
    if (range) {
      if (!originalStart) originalStart = formatClock12FromMinutes(range.start);
      if (!originalEnd) originalEnd = formatClock12FromMinutes(range.end);
    }
  }

  if (!originalStart || !originalEnd || !startTime || !endTime) {
    return null;
  }

  return {
    ...update,
    startTime,
    endTime,
    originalStart,
    originalEnd,
  };
}

/** Build notification payloads from computeCompressibleTimelineShiftUpdates output. */
export function buildAdjustmentsFromChainUpdates(chainUpdates, sourceById, formattedDate) {
  const adjustments = [];
  for (const u of chainUpdates) {
    const src = sourceById.get(u.id);
    if (!src) continue;
    const enriched = enrichChainUpdateWithOriginalTimes(u, src);
    if (!enriched) continue;
    adjustments.push({
      id: enriched.id,
      startTime: enriched.startTime,
      endTime: enriched.endTime,
      originalStart: enriched.originalStart,
      originalEnd: enriched.originalEnd,
      patientId: src.patientId || src.uid || src.userId,
      date: src.date || formattedDate,
      patientDisplay: src.patientDisplay || src.patientName || src.name,
      insuranceDetails: src.insuranceDetails,
      hasInsurance: src.hasInsurance,
      status: src.status || "Pending",
      dentistId: src.dentistId,
      dentistName: src.dentist || src.dentistName || "",
      services: pickServicesFromAppointmentRecord(src),
    });
  }
  return adjustments;
}

/**
 * Persist timeline compression shifts, then notify (patient + assigned dentist + Clinic Staff).
 * Uses originalStart/originalEnd from chainUpdates — safe even if Firestore already updated elsewhere.
 */
export async function applyCompressibleTimelineShiftsAndNotify({
  appointmentRef,
  chainUpdates,
  sourceAppointments,
  formattedDate,
  skipNotifications = false,
}) {
  if (!chainUpdates?.length || !appointmentRef) {
    return [];
  }

  const sourceById = new Map(
    (sourceAppointments || []).map((ap) => [ap.id, ap])
  );
  const adjustments = buildAdjustmentsFromChainUpdates(
    chainUpdates,
    sourceById,
    formattedDate
  );
  if (adjustments.length === 0) {
    return [];
  }

  const batchUpdates = {};
  for (const row of adjustments) {
    batchUpdates[`${row.id}.startTime`] = row.startTime;
    batchUpdates[`${row.id}.endTime`] = row.endTime;
    batchUpdates[`${row.id}.time`] = row.startTime;
    const startM = parseClockLabelToMinutes(row.startTime);
    const endM = parseClockLabelToMinutes(row.endTime);
    if (Number.isFinite(startM) && Number.isFinite(endM) && endM > startM) {
      batchUpdates[`${row.id}.duration`] = endM - startM;
    }
  }

  await updateDoc(appointmentRef, batchUpdates);
  if (!skipNotifications) {
    await sendPendingAppointmentTimeAdjustmentNotificationsBatch(adjustments);
  }
  return adjustments;
}

/**
 * Notify affected patient (app + email) and assigned dentist + all Clinic Staff (app + email)
 * when a pending appointment's start/end time was shifted.
 */
export async function sendPendingAppointmentTimeAdjustmentNotifications(
  adjustment,
  options = {}
) {
  const { usersSnapshot = null, skipDedupe = false } = options;

  if (!skipDedupe && shouldSkipDuplicateNotify(adjustment)) {
    return;
  }

  const {
    patientId,
    date,
    originalStart: rawOriginalStart,
    originalEnd: rawOriginalEnd,
    startTime: rawStartTime,
    endTime: rawEndTime,
    dentistId,
    hasInsurance,
    insuranceDetails,
    status,
    patientDisplay,
    patientName: patientNameHint,
    services,
  } = adjustment;

  let usersSnapshotForNotify = usersSnapshot;
  if (!usersSnapshotForNotify) {
    try {
      usersSnapshotForNotify = await getDocs(collection(db, "users"));
    } catch (error) {
      console.error("Error loading users for time-adjustment notifications:", error);
    }
  }

  const dentistName = resolveDentistDisplayName(adjustment, usersSnapshotForNotify);
  const servicesText = formatServicesForEmail(services);

  const originalStart = formatClockLabelForDisplay(rawOriginalStart);
  const originalEnd = formatClockLabelForDisplay(rawOriginalEnd);
  const startTime = formatClockLabelForDisplay(rawStartTime);
  const endTime = formatClockLabelForDisplay(rawEndTime);

  if (!originalStart || !originalEnd || !startTime || !endTime) {
    return;
  }
  if (
    clockLabelsEqual(rawOriginalStart, rawStartTime) &&
    clockLabelsEqual(rawOriginalEnd, rawEndTime)
  ) {
    return;
  }

  const formattedDateLong = formatDateLong(date);
  const originalWindow = buildNotificationWindow(
    formattedDateLong,
    originalStart,
    originalEnd
  );
  const insuranceText = hasInsurance
    ? insuranceDetails?.companyName || "Yes"
    : "None";
  const statusText = status || "Pending";

  let patientNameForPersonnel =
    patientNameHint || patientDisplay || "Patient";

  const resolvedPatientId = patientId || null;

  if (resolvedPatientId) {
    const patientMessage = `Your appointment on ${originalWindow} has been updated. The time has been updated from ${originalStart} - ${originalEnd} to ${startTime} - ${endTime}.\n\nPlease note: No response or no reply will automatically mean that you accept the new schedule.\n\nIf you think you will not be able to attend the updated time, we suggest rescheduling or cancelling your appointment.\n\nInsurance: ${insuranceText}\nStatus: ${statusText}`;

    await addNotification(resolvedPatientId, patientMessage);

    try {
      const patientDoc = await getDoc(doc(db, "users", resolvedPatientId));
      if (patientDoc.exists()) {
        const patientData = patientDoc.data();
        patientNameForPersonnel =
          patientData.firstName && patientData.lastName
            ? `${patientData.firstName} ${patientData.lastName}`
            : patientData.name || patientNameForPersonnel;

        const patientEmail = resolveNotificationEmail(patientData);
        if (patientEmail && isEmailVerified(patientData)) {
          void sendAutoAdjustmentEmail({
            type: "patient",
            recipientEmail: patientEmail,
            recipientName: patientNameForPersonnel,
            patientName: patientNameForPersonnel,
            appointmentDate: formattedDateLong,
              appointmentDateKey: adjustment.date,
            originalStartTime: originalStart,
            originalEndTime: originalEnd,
            adjustedStartTime: startTime,
            adjustedEndTime: endTime,
            startTime,
            endTime,
            dentistName,
            services: servicesText,
            insuranceText,
            appointmentId: adjustment.id,
          });
        } else if (!patientEmail) {
          console.warn(
            `Patient ${resolvedPatientId} has no email for time-update notification`
          );
        } else if (!isEmailVerified(patientData)) {
          console.warn(
            `Patient ${resolvedPatientId} email (${patientEmail}) is not verified; skipping time-update email`
          );
        }
      }
    } catch (error) {
      console.error("Error sending patient time-adjustment notifications:", error);
    }
  }

  const personnelMessage = `The appointment for ${patientNameForPersonnel} on ${originalWindow} has been updated. The time has been updated from ${originalStart} - ${originalEnd} to ${startTime} - ${endTime}.\n\nInsurance: ${insuranceText}\nStatus: ${statusText}`;

  try {
    const snapshot = usersSnapshotForNotify;
    const notifiedUserIds = new Set();

    for (const docu of snapshot.docs) {
      const data = docu.data();
      const userId = docu.id;
      const userRole = data.userType || data.role;
      const isAssignedDentist = dentistId && userId === dentistId;
      const isClinicStaff = userRole === "ClinicStaff";

      if (!isAssignedDentist && !isClinicStaff) {
        continue;
      }
      if (notifiedUserIds.has(userId)) {
        continue;
      }
      notifiedUserIds.add(userId);

      await addNotification(userId, personnelMessage);

      const personnelEmail = resolveNotificationEmail(data);
      if (!personnelEmail || !isEmailVerified(data)) {
        continue;
      }

      const personnelName =
        data.firstName && data.lastName
          ? `${data.firstName} ${data.lastName}`
          : data.name || "Staff";

      void sendAutoAdjustmentEmail({
        type: "personnel",
        recipientEmail: personnelEmail,
        recipientName: personnelName,
        recipientRole: userRole,
        patientName: patientNameForPersonnel,
        appointmentDate: formattedDateLong,
        appointmentDateKey: adjustment.date,
        originalStartTime: originalStart,
        originalEndTime: originalEnd,
        adjustedStartTime: startTime,
        adjustedEndTime: endTime,
        startTime,
        endTime,
        dentistName,
        services: servicesText,
        insuranceText,
        appointmentId: adjustment.id,
      });
    }
  } catch (error) {
    console.error("Error sending personnel time-adjustment notifications:", error);
  }
}

/** One users fetch for many shifted appointments (booking / save compression). */
export async function sendPendingAppointmentTimeAdjustmentNotificationsBatch(
  adjustments
) {
  if (!Array.isArray(adjustments) || adjustments.length === 0) {
    return;
  }

  const unique = [];
  const seenIds = new Set();
  for (const row of adjustments) {
    if (seenIds.has(row.id)) continue;
    seenIds.add(row.id);
    if (shouldSkipDuplicateNotify(row)) continue;
    unique.push(row);
  }
  if (unique.length === 0) {
    return;
  }

  let usersSnapshot = null;
  try {
    usersSnapshot = await getDocs(collection(db, "users"));
  } catch (error) {
    console.error("Error loading users for time-adjustment notifications:", error);
  }

  for (const adjustment of unique) {
    await sendPendingAppointmentTimeAdjustmentNotifications(adjustment, {
      usersSnapshot,
      skipDedupe: true,
    });
  }
}
